import { useSetAtom } from 'jotai';

import type { User } from '@/@types/global';
import type { Appointment } from '@/react-query/useAppointments';

import {
  appointmentDataWithResetAtom,
  isRecorderSheetOpenAtom,
  uiStatusAtom,
  voiceNotesSourceAtom,
} from './state';
import { VideaNotesUIState, type VoiceNotesSource } from './types';

// Re-export for backward compatibility
export { VideaNotesUIState };

// Extend the original Appointment type to support both regular appointments and standalone visits
export type VideaNotesAppointment = Appointment & {
  appointment: Appointment['appointment'] & {
    /**
     * Make unifiedId optional to support standalone visits
     */
    unifiedId?: string;
    /**
     * Add support for standalone patient visits
     */
    standalonePatientVisitId?: string;
  };
};

export const useOpenVideaNotes = () => {
  const setAppointmentDataForVideaNotes = useSetAtom(
    appointmentDataWithResetAtom,
  );
  const setIsVideaNotesSheetOpen = useSetAtom(isRecorderSheetOpenAtom);
  const setUiStatus = useSetAtom(uiStatusAtom);
  const setVoiceNotesSource = useSetAtom(voiceNotesSourceAtom);

  return (
    user: User,
    appointment: VideaNotesAppointment,
    uiState: VideaNotesUIState,
    voiceNotesSource: VoiceNotesSource,
  ) => {
    // Set appointment data first (this will reset UI state to AUDIO_CAPTURE)
    const appointmentData = {
      appointmentStartsAt: appointment.appointment.start,
      patient: appointment.patient,
      estimatedVisitId: appointment.appointment.estimatedVisitId,
      standalonePatientVisitId:
        appointment.appointment.standalonePatientVisitId,
      unifiedId: appointment.appointment.unifiedId,
      loggedInUser: user.username ?? '',
    };

    setAppointmentDataForVideaNotes(appointmentData);

    // Set the UI state after appointment data
    setUiStatus(uiState);

    setIsVideaNotesSheetOpen(true);

    setVoiceNotesSource(voiceNotesSource);
  };
};
